import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CalendarClock, ArrowLeft, Save, ChevronLeftIcon } from "lucide-react";
import { DatePicker, Select } from "antd";
import dayjs from "dayjs";

import { ContainerCard, ModuleTitleCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { Input, Textarea } from "../../components/ui/input";
import ToastMsg from "../../components/common/ToastMsg";
import useApiMethods from "../../lib/useApiMethods";
import { APPRAISAL_CYCLES } from "../../constants/api";

import useAppraisalRole, { useAppraisalPermissions } from "../../hooks/useAppraisalRole";

const emptyCycle = {
    name: "",
    description: "",
    period_start: null,
    period_end: null,
    self_eval_start: null,
    self_eval_end: null,
    rm_review_deadline: null,
    hr_review_deadline: null,
    status: "draft",
};

const AppraisalCycleAddEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;
    const { fetch, create, update } = useApiMethods();
    const { isHR } = useAppraisalRole();
    const { canAddCycle, canEditCycle } = useAppraisalPermissions();

    const allowed = isEdit ? canEditCycle : canAddCycle;

    const [form, setForm] = useState(emptyCycle);
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!isEdit) return;
        let cancelled = false;
        setLoading(true);
        fetch(APPRAISAL_CYCLES, { id })
            .then((res) => { if (!cancelled && res?.data) setForm(res.data); })
            .catch(() => { if (!cancelled) ToastMsg("error", "Failed to load cycle"); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit]);

    if (!isHR && !allowed) {
        return (
            <ContainerCard>
                <div className="text-center py-12 text-muted-foreground">
                    Only HR can create or edit appraisal cycles.
                </div>
            </ContainerCard>
        );
    }

    const setField = (key, value) => {
        setForm((prev) => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors((e) => ({ ...e, [key]: undefined }));
    };

    const validate = () => {
        const e = {};
        if (!form.name?.trim()) e.name = "Name is required.";
        if (!form.period_start) e.period_start = "Period start is required.";
        if (!form.period_end) e.period_end = "Period end is required.";
        if (form.period_start && form.period_end && dayjs(form.period_end).isBefore(dayjs(form.period_start))) {
            e.period_end = "Period end must be after start.";
        }
        if (!form.self_eval_start) e.self_eval_start = "Self-eval start is required.";
        if (!form.self_eval_end) e.self_eval_end = "Self-eval end is required.";
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSave = async () => {
        if (!validate()) {
            ToastMsg("error", "Please fix the highlighted fields");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form };
            const res = isEdit
                ? await update(APPRAISAL_CYCLES, { ...payload, id: Number(id) })
                : await create(APPRAISAL_CYCLES, payload);
            if (res?.status === "success") {
                ToastMsg("success", res.message || `Cycle ${isEdit ? "updated" : "created"}`);
                navigate("/appraisal/cycles");
            } else {
                ToastMsg("error", res?.message || "Failed to save cycle");
            }
        } catch (_) {
            ToastMsg("error", "Failed to save cycle");
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate("/appraisal/cycles");
    }

    return (
        <ContainerCard>
            <div className="flex items-center gap-4 mb-6">
                <ChevronLeftIcon className="h-5 w-5 cursor-pointer" onClick={handleBack} />
                <div className="flex-1">
                    <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                        {isEdit ? "Edit Appraisal Cycle" : "Add Appraisal Cycle"}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1">
                        Define the period, self-evaluation window, and review deadlines
                    </p>
                </div>
            </div>

            <div className="px-3 pb-6">
                {loading ? (
                    <div className="text-center py-10 text-muted-foreground">Loading…</div>
                ) : (
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 sm:p-6 space-y-5 max-w-full">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Cycle Name <span className="text-red-500">*</span>
                                </label>
                                <Input
                                    placeholder="e.g. Q3 2026 Review"
                                    value={form.name || ""}
                                    onChange={(e) => setField("name", e.target.value)}
                                />
                                {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
                            </div>

                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <Textarea
                                    rows={2}
                                    placeholder="Optional notes about this cycle..."
                                    value={form.description || ""}
                                    onChange={(e) => setField("description", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Period Start <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.period_start ? dayjs(form.period_start) : null}
                                    onChange={(_, str) => setField("period_start", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                                {errors.period_start && <p className="text-xs text-red-500 mt-1">{errors.period_start}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Period End <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.period_end ? dayjs(form.period_end) : null}
                                    onChange={(_, str) => setField("period_end", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                                {errors.period_end && <p className="text-xs text-red-500 mt-1">{errors.period_end}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Self-Eval Start <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.self_eval_start ? dayjs(form.self_eval_start) : null}
                                    onChange={(_, str) => setField("self_eval_start", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                                {errors.self_eval_start && <p className="text-xs text-red-500 mt-1">{errors.self_eval_start}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Self-Eval End <span className="text-red-500">*</span>
                                </label>
                                <DatePicker
                                    value={form.self_eval_end ? dayjs(form.self_eval_end) : null}
                                    onChange={(_, str) => setField("self_eval_end", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                                {errors.self_eval_end && <p className="text-xs text-red-500 mt-1">{errors.self_eval_end}</p>}
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">RM Review Deadline</label>
                                <DatePicker
                                    value={form.rm_review_deadline ? dayjs(form.rm_review_deadline) : null}
                                    onChange={(_, str) => setField("rm_review_deadline", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">HR Review Deadline</label>
                                <DatePicker
                                    value={form.hr_review_deadline ? dayjs(form.hr_review_deadline) : null}
                                    onChange={(_, str) => setField("hr_review_deadline", str || null)}
                                    format="YYYY-MM-DD"
                                    className="w-full h-10"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                                <Select
                                    value={form.status}
                                    onChange={(v) => setField("status", v)}
                                    options={[
                                        { value: "draft", label: "Draft" },
                                        { value: "active", label: "Active" },
                                        { value: "closed", label: "Closed" },
                                    ]}
                                    className="w-full"
                                    size="large"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
                            <Button variant="outline" onClick={() => navigate("/appraisal/cycles")}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSave}
                                disabled={saving}
                                className="bg-sidebar hover:bg-sidebar-primary text-white"
                            >
                                <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : (isEdit ? "Update Cycle" : "Create Cycle")}
                            </Button>
                        </div>
                    </div>
                )}
            </div>
        </ContainerCard>
    );
};

export default AppraisalCycleAddEdit;
